import type { EditRegion } from '@/types/render';

/**
 * Colours of edit regions by position. The SERVER assigns them
 * (App\Services\Render\EditRegions\EditRegionPalette) and snapshots them on
 * `render.settings.edit_regions`; this mirror only paints the draft before it
 * is sent. Same fixed order, so the draft and the finished render agree.
 */
export const EDIT_REGION_COLORS = [
  '#FF2D2D',
  '#1E6BFF',
  '#FFD400',
  '#19C24A',
  '#E62EE6',
] as const;

/** Server cap (EditRegionPalette::MAX_REGIONS). */
export const MAX_EDIT_REGIONS = EDIT_REGION_COLORS.length;

/** Draft colour of the region at `index` (the slot past the end reuses the last). */
export function editRegionColor(index: number): string {
  return (
    EDIT_REGION_COLORS[Math.min(index, MAX_EDIT_REGIONS - 1)] ??
    EDIT_REGION_COLORS[0]
  );
}

/** Smallest side a region may have, as a share of the image. */
const MIN_REGION_SIZE = 0.02;
/** Server accepts up to 200 lasso points. */
const MAX_LASSO_POINTS = 200;

export type Point = [number, number];

export function clampPoint(x: number, y: number): Point {
  return [
    Math.round(Math.min(1, Math.max(0, x)) * 10000) / 10000,
    Math.round(Math.min(1, Math.max(0, y)) * 10000) / 10000,
  ];
}

/** Polygon vertices for drawing: a rect expands its two corners to four. */
export function regionPolygon(
  region: Pick<EditRegion, 'shape' | 'points'>
): Point[] {
  const [a, b] = region.points;
  if (region.shape !== 'rect' || !a || !b) return region.points;
  const [x1, y1] = a;
  const [x2, y2] = b;
  return [
    [x1, y1],
    [x2, y1],
    [x2, y2],
    [x1, y2],
  ];
}

export function regionBounds(points: Point[]) {
  const xs = points.map((p) => p[0]);
  const ys = points.map((p) => p[1]);
  return {
    minX: Math.min(...xs),
    maxX: Math.max(...xs),
    minY: Math.min(...ys),
    maxY: Math.max(...ys),
  };
}

/**
 * Turn a finished gesture into a region shape, or null when it is too small
 * to mean anything (a stray tap). A lasso is thinned to the server's point cap.
 */
export function finalizeGesture(
  shape: EditRegion['shape'],
  points: Point[]
): Pick<EditRegion, 'shape' | 'points'> | null {
  if (points.length < 2) return null;
  const b = regionBounds(points);
  if (b.maxX - b.minX < MIN_REGION_SIZE || b.maxY - b.minY < MIN_REGION_SIZE) {
    return null;
  }
  if (shape === 'rect') {
    return {
      shape,
      points: [
        [b.minX, b.minY],
        [b.maxX, b.maxY],
      ],
    };
  }
  if (
    points.length < 3 ||
    polygonArea(points) < MIN_REGION_SIZE * MIN_REGION_SIZE
  ) {
    return null; // a scribble along a line encloses nothing
  }
  const step = Math.ceil(points.length / MAX_LASSO_POINTS);
  return { shape, points: points.filter((_, i) => i % step === 0) };
}

/** Enclosed area (shoelace), as a share of the image. */
function polygonArea(points: Point[]): number {
  let sum = 0;
  points.forEach(([x1, y1], i) => {
    const [x2, y2] = points[(i + 1) % points.length] ?? [x1, y1];
    sum += x1 * y2 - x2 * y1;
  });
  return Math.abs(sum) / 2;
}

/** SVG `points` attribute in the 0..1 viewBox. */
export function svgPoints(points: Point[]): string {
  return points.map(([x, y]) => `${x},${y}`).join(' ');
}

/**
 * The visible window of an image drawn with cover-fit (object-cover) into a
 * frame, in normalised IMAGE coordinates. The frame takes the view photo's
 * aspect (clamped), so a render of another aspect is centre-cropped: region
 * points are shares of the whole image (the server draws them on it), while
 * the user draws on the cropped window. Unknown image size → no crop.
 */
export type CoverWindow = { x: number; y: number; w: number; h: number };

export function coverWindow(
  frameW: number,
  frameH: number,
  imageW: number | null | undefined,
  imageH: number | null | undefined
): CoverWindow {
  if (!imageW || !imageH || frameW <= 0 || frameH <= 0) {
    return { x: 0, y: 0, w: 1, h: 1 };
  }
  const frameAspect = frameW / frameH;
  const imageAspect = imageW / imageH;
  const w = imageAspect > frameAspect ? frameAspect / imageAspect : 1;
  const h = imageAspect > frameAspect ? 1 : imageAspect / frameAspect;
  return { x: (1 - w) / 2, y: (1 - h) / 2, w, h };
}

/** Frame share (0..1 of the visible frame) → image share. */
export function frameToImage(win: CoverWindow, fx: number, fy: number): Point {
  return clampPoint(win.x + fx * win.w, win.y + fy * win.h);
}

/** Image share → frame share (may fall outside 0..1 for cropped parts). */
export function imageToFrame(win: CoverWindow, [x, y]: Point): Point {
  return [(x - win.x) / win.w, (y - win.y) / win.h];
}
